"use client";

import {
  ComposerAddAttachment,
  ComposerAttachments,
  UserMessageAttachments,
} from "@/components/assistant-ui/attachment";
import { File } from "@/components/assistant-ui/file";
import { ThreadFollowupSuggestions } from "@/components/assistant-ui/follow-up-suggestions";
import { Image } from "@/components/assistant-ui/image";
import { MarkdownText } from "@/components/assistant-ui/markdown-text";
import {
  Reasoning,
  ReasoningContent,
  ReasoningRoot,
  ReasoningText,
  ReasoningTrigger,
} from "@/components/assistant-ui/reasoning";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import {
  ToolGroupBlock,
  ToolGroupThinkingContext,
  useToolGroupCurrentToolName,
  useToolGroupLiveState,
} from "@/components/assistant-ui/tool-group";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { MessageFeedbackButtons, MessageFeedbackHost } from "@/components/assistant-ui/message-feedback";
import { MessageTimestamp } from "@/components/assistant-ui/message-timestamp";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  ActionBarPrimitive,
  AuiIf,
  type AssistantState,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  groupPartByType,
  MessagePrimitive,
  SuggestionPrimitive,
  ThreadPrimitive,
  type FileMessagePartComponent,
  type ImageMessagePartComponent,
  type TextMessagePartComponent,
  type ToolCallMessagePartComponent,
  useAuiState,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  MicIcon,
  PencilIcon,
  RefreshCwIcon,
  SquareIcon,
} from "lucide-react";
import { ComposerSendIcon } from "@/components/assistant-ui/composer-send-icon";
import {
  createContext,
  useContext,
  useState,
  type ComponentType,
  type FC,
  type PropsWithChildren,
} from "react";
import { displaySkillUserMessage } from "@/app/Mcp/utils/skillMarkers";
import { displayPromptTemplateUserMessage } from "@/app/Mcp/utils/promptTemplateMarkers";
import { displaySuggestionUserMessage } from "@/app/Mcp/utils/suggestionMarkers";
import { displayDomainMentionUserMessage } from "@/app/Mcp/utils/domainMentionMarkers";

export type ThreadGroupPart = MessagePrimitive.GroupedParts.GroupPart;

/**
 * Optional component overrides for the thread. `AssistantMessage` and
 * `Welcome` replace whole sections; the remaining slots override how the
 * assistant message renders tool calls and part groups. Tool UIs registered
 * by name (toolkit `render`, `useAssistantDataUI`) take precedence over
 * `ToolFallback`.
 */
export type ThreadComponents = {
  AssistantMessage?: ComponentType | undefined;
  Welcome?: ComponentType | undefined;
  /** Replaces the default welcome FAQ chips under the composer. */
  Suggestions?: ComponentType | undefined;
  /**
   * Replaces follow-up chips above the composer (after the latest assistant
   * reply). Defaults to assistant-ui thread suggestions.
   */
  FollowupSuggestions?: ComponentType | undefined;
  /**
   * Optional content under an assistant message body (e.g. FOLLOWUPS chips).
   * Rendered before the message action footer.
   */
  MessageFollowups?: ComponentType | undefined;
  /** Optional overlay above the composer input (e.g. `/` slash skills menu). */
  ComposerAddon?: ComponentType | undefined;
  /**
   * Inline chips inside the composer input row (e.g. `@` domain mention badges).
   */
  ComposerMentions?: ComponentType | undefined;
  /** Renders just before the send/cancel controls (e.g. model picker). */
  ComposerBeforeSend?: ComponentType | undefined;
  /**
   * Replaces the default composer "+" attachment button (e.g. plus menu).
   */
  ComposerLeadingAction?: ComponentType | undefined;
  /**
   * Optional toolbar under the composer input/actions (e.g. domain picker),
   * styled like Cursor's workspace row.
   */
  ComposerFooter?: ComponentType | undefined;
  /** Extra assistant action-bar control (e.g. export PDF). */
  AssistantActionExtra?: ComponentType | undefined;
  /** Replaces markdown for an assistant text part (e.g. budget warning alert). */
  AssistantText?: ComponentType | undefined;
  /** Optional note under the thinking indicator (e.g. slow Auto model hint). */
  ThinkingHint?: ComponentType | undefined;
  ToolFallback?: ToolCallMessagePartComponent | undefined;
  ToolGroup?:
    | ComponentType<PropsWithChildren<{ group: ThreadGroupPart }>>
    | undefined;
  ReasoningGroup?:
    | ComponentType<PropsWithChildren<{ group: ThreadGroupPart }>>
    | undefined;
};

export type ThreadProps = {
  components?: ThreadComponents | undefined;
  /** When false, the composer does not take focus on mount. */
  composerAutoFocus?: boolean;
};

const EMPTY_COMPONENTS: ThreadComponents = {};

const ThreadComponentsContext =
  createContext<ThreadComponents>(EMPTY_COMPONENTS);

const ComposerAutoFocusContext = createContext(true);

// Startup exposes a loading placeholder thread; treat it as a new chat so
// the composer mounts centered. Loads after startup keep the docked layout.
const isNewChatView = (s: AssistantState) =>
  s.thread.messages.length === 0 &&
  (!s.thread.isLoading || s.threads.isLoading);

// A switched thread that is still fetching its history: skeleton, not welcome.
const isHistoryLoadingView = (s: AssistantState) =>
  s.thread.messages.length === 0 &&
  s.thread.isLoading &&
  !s.thread.isDisabled &&
  !s.threads.isLoading;

const ThreadHistorySkeleton: FC = () => (
  <div
    data-slot="aui_thread-history-skeleton"
    role="status"
    className="animate-in fade-in fill-mode-both flex flex-col gap-y-6 [animation-delay:150ms] [animation-duration:200ms]"
  >
    <span className="sr-only">Loading conversation</span>
    <Skeleton className="ml-auto h-9 w-2/5 rounded-xl motion-reduce:animate-none" />
    <div className="flex flex-col gap-y-2">
      <Skeleton className="h-4 w-11/12 motion-reduce:animate-none" />
      <Skeleton className="h-4 w-4/5 motion-reduce:animate-none" />
      <Skeleton className="h-4 w-3/5 motion-reduce:animate-none" />
    </div>
    <Skeleton className="ml-auto h-9 w-1/3 rounded-xl motion-reduce:animate-none" />
    <div className="flex flex-col gap-y-2">
      <Skeleton className="h-4 w-10/12 motion-reduce:animate-none" />
      <Skeleton className="h-4 w-2/3 motion-reduce:animate-none" />
    </div>
  </div>
);

export const Thread: FC<ThreadProps> = ({
  components = EMPTY_COMPONENTS,
  composerAutoFocus = true,
}) => {
  const isEmpty = useAuiState(isNewChatView);

  return (
    <ComposerAutoFocusContext.Provider value={composerAutoFocus}>
      <ThreadComponentsContext.Provider value={components}>
        <ThreadRoot isEmpty={isEmpty} />
      </ThreadComponentsContext.Provider>
    </ComposerAutoFocusContext.Provider>
  );
};

const ThreadRoot: FC<{ isEmpty: boolean }> = ({ isEmpty }) => {
  const {
    Welcome = ThreadWelcome,
    Suggestions: SuggestionsComponent = ThreadSuggestions,
    FollowupSuggestions:
      FollowupSuggestionsComponent = ThreadFollowupSuggestions,
  } = useContext(ThreadComponentsContext);

  const viewportInnerClass = cn(
    "mx-auto flex w-full max-w-(--thread-max-width) min-w-0 flex-1 flex-col px-4 sm:px-6",
    isEmpty ? "min-h-full pt-0" : "pt-4",
  );

  const emptyShellClass = cn(
    "mx-auto box-border grid h-full min-h-full w-full max-w-(--thread-max-width) min-w-0 flex-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] px-4 pt-0 pb-(--mcp-chat-header-height) sm:px-6",
  );

  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root bg-background @container relative flex h-full flex-col"
      style={{
        ["--thread-max-width" as string]: "50rem",
        ["--composer-bg" as string]: "var(--color-card)",
        ["--composer-radius" as string]: "1.75rem",
        ["--composer-padding" as string]: "8px",
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        data-slot="aui_thread-viewport"
        className="relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth"
      >
        {isEmpty ? (
          <div className="relative h-full min-h-full w-full flex-1">
            <div className={emptyShellClass}>
              <div aria-hidden className="min-h-0" />
              <div className="flex flex-col gap-6 py-1">
                <AuiIf condition={isNewChatView}>
                  <Welcome />
                </AuiIf>
                <div className="relative flex w-full flex-col gap-4">
                  <FollowupSuggestionsComponent />
                  <Composer />
                </div>
              </div>
              <div aria-hidden className="min-h-0" />
            </div>
            <AuiIf condition={isNewChatView}>
              <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-(--thread-max-width) px-4 pb-6 sm:px-6">
                <SuggestionsComponent />
              </div>
            </AuiIf>
          </div>
        ) : (
          <div className={viewportInnerClass}>
            <AuiIf condition={isHistoryLoadingView}>
              <ThreadHistorySkeleton />
            </AuiIf>

            <div
              data-slot="aui_message-group"
              className="mb-14 flex flex-col gap-y-6 empty:hidden"
            >
              <ThreadPrimitive.Messages>
                {() => <ThreadMessage />}
              </ThreadPrimitive.Messages>
            </div>

            <ThreadPrimitive.ViewportFooter
              className="aui-thread-viewport-footer bg-background relative mt-auto flex flex-col gap-4 overflow-visible rounded-t-(--composer-radius) pb-4 md:pb-6 sticky bottom-0"
            >
              <div className="pointer-events-none absolute inset-x-0 -top-11 z-30 flex justify-center">
                <ThreadScrollToBottom />
              </div>
              <FollowupSuggestionsComponent />
              <Composer compact />
            </ThreadPrimitive.ViewportFooter>
          </div>
        )}
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ThreadMessage: FC = () => {
  const { AssistantMessage: AssistantMessageComponent = AssistantMessage } =
    useContext(ThreadComponentsContext);
  const role = useAuiState((s) => s.message.role);
  const isEditing = useAuiState((s) => s.message.composer.isEditing);

  if (isEditing) return <EditComposer />;
  if (role === "user") return <UserMessage />;
  return <AssistantMessageComponent />;
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="رفتن به آخر گفتگو"
        side="top"
        variant="outline"
        className="aui-thread-scroll-to-bottom pointer-events-auto dark:border-border dark:bg-background dark:hover:bg-accent z-20 rounded-full disabled:invisible"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  return (
    <div className="aui-thread-welcome-root flex flex-col items-center px-4 text-center">
      <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-2xl font-medium tracking-tight duration-200">
        چطور می‌تونم کمکتون کنم؟
      </h1>
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestions flex w-full flex-wrap items-center justify-center gap-2 px-4">
      <ThreadPrimitive.Suggestions>
        {() => <ThreadSuggestionItem />}
      </ThreadPrimitive.Suggestions>
    </div>
  );
};

const ThreadSuggestionItem: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200">
      <SuggestionPrimitive.Trigger send asChild>
        <Button
          variant="ghost"
          className="aui-thread-welcome-suggestion text-foreground hover:bg-muted border-border/60 h-auto gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-normal whitespace-nowrap transition-colors"
        >
          <SuggestionPrimitive.Title className="aui-thread-welcome-suggestion-text-1" />
          <SuggestionPrimitive.Description className="aui-thread-welcome-suggestion-text-2 empty:hidden" />
        </Button>
      </SuggestionPrimitive.Trigger>
    </div>
  );
};

const Composer: FC<{ compact?: boolean }> = ({ compact = false }) => {
  const {
    ComposerAddon,
    ComposerMentions,
    ComposerFooter,
    ComposerLeadingAction
  } = useContext(ThreadComponentsContext);
  const composerAutoFocus = useContext(ComposerAutoFocusContext);
  const LeadingAction = ComposerLeadingAction ?? ComposerAddAttachment;

  return (
    <ComposerPrimitive.Root className="aui-composer-root relative flex w-full min-w-0 flex-col gap-2 self-stretch">
      <div className="relative w-full min-w-0">
        <div
          data-slot="aui_composer-cluster"
          className={cn(
            'aui-composer-cluster flex w-full min-w-0 flex-col',
            ComposerFooter && 'has-footer'
          )}
        >
          <ComposerPrimitive.AttachmentDropzone asChild>
            <div
              data-slot="aui_composer-shell"
              className={cn(
                'flex w-full min-w-0 cursor-text flex-col rounded-[1.75rem] transition-[border-color] data-[dragging=true]:border-dashed',
                ComposerFooter
                  ? 'border-0'
                  : 'border-border/70 focus-within:border-border data-[dragging=true]:border-ring dark:border-white/15 dark:focus-within:border-white/25 border'
              )}
            >
              <div className="flex min-w-0 w-full flex-col px-2 pt-2">
                <ComposerAttachments />
                <div
                  className="flex min-w-0 w-full flex-wrap items-start gap-1.5 px-1.5"
                  dir="rtl"
                >
                  {ComposerMentions ? <ComposerMentions /> : null}
                  <ComposerPrimitive.Input
                    placeholder="هر چیزی بپرسید... (با / مهارت‌ها، با @ دامنه‌ها)"
                    className={cn(
                      'aui-composer-input caret-primary placeholder:text-muted-foreground/55 max-h-48 min-w-[8rem] flex-1 resize-none bg-transparent px-0 py-2.5 text-[15px] leading-6 outline-none',
                      compact ? 'is-compact' : 'min-h-[4.25rem]'
                    )}
                    rows={1}
                    autoFocus={composerAutoFocus}
                    enterKeyHint="send"
                    aria-label="Message input"
                  />
                </div>
              </div>
              <div className="aui-composer-bottom-bar flex w-full min-w-0 items-center justify-between gap-2 px-2 pb-2">
                <LeadingAction />
                <ComposerAction />
              </div>
            </div>
          </ComposerPrimitive.AttachmentDropzone>
          {ComposerFooter ? (
            <div className="aui-composer-footer flex w-full min-w-0 items-center gap-2 px-3 py-2">
              <ComposerFooter />
            </div>
          ) : null}
        </div>
        {ComposerAddon ? <ComposerAddon /> : null}
      </div>
      <p className="text-center text-600 fs--2 mb-0">
        دستیار ممکن است اشتباه کند. اطلاعات مهم را بررسی کنید.
      </p>
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC = () => {
  const { ComposerBeforeSend } = useContext(ThreadComponentsContext);
  return (
    <div
      className="aui-composer-action-wrapper relative flex shrink-0 items-center gap-1 self-center"
    >
      {ComposerBeforeSend ? <ComposerBeforeSend /> : null}
      <AuiIf condition={(s) => s.thread.capabilities.dictation}>
        <AuiIf condition={(s) => s.composer.dictation == null}>
          <ComposerPrimitive.Dictate asChild>
            <TooltipIconButton
              tooltip="ورودی صوتی"
              side="bottom"
              type="button"
              variant="ghost"
              size="icon"
              className="aui-composer-dictate text-muted-foreground hover:text-foreground size-6 rounded-full"
              aria-label="Start voice input"
            >
              <MicIcon className="aui-composer-dictate-icon size-3.5" />
            </TooltipIconButton>
          </ComposerPrimitive.Dictate>
        </AuiIf>
        <AuiIf condition={(s) => s.composer.dictation != null}>
          <ComposerPrimitive.StopDictation asChild>
            <TooltipIconButton
              tooltip="توقف دیکته"
              side="bottom"
              type="button"
              variant="ghost"
              size="icon"
              className="aui-composer-stop-dictation text-destructive size-6 rounded-full"
              aria-label="Stop voice input"
            >
              <SquareIcon className="aui-composer-stop-dictation-icon size-3.5 animate-pulse fill-current" />
            </TooltipIconButton>
          </ComposerPrimitive.StopDictation>
        </AuiIf>
      </AuiIf>
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="ارسال"
            side="bottom"
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-send size-8 rounded-full"
            aria-label="Send message"
          >
            <ComposerSendIcon className="size-4" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-8 rounded-full"
            aria-label="Stop generating"
          >
            <SquareIcon className="aui-composer-cancel-icon size-4 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

const CHAT_NETWORK_ERROR_MESSAGE =
  "ارتباط با سرور برقرار نشد. لطفاً دوباره تلاش کنید.";

function isRawFetchFailure(message: string): boolean {
  return /failed to fetch|networkerror when attempting to fetch resource|network request failed|^load failed$/i.test(
    message.trim(),
  );
}

const DEFAULT_THINKING_LABEL = "در حال فکر کردن";

/** Thinking label; comes from the backend `status` event when present. */
export function useThinkingLabel(): string {
  const statusText = useAuiState((s) => {
    const value = s.message.metadata?.custom?.statusText;
    return typeof value === "string" ? value : undefined;
  });
  // Trailing ellipsis is replaced by the animated dots.
  return (
    statusText?.replace(/(\s*(…|\.{2,}))+\s*$/, "").trim() ||
    DEFAULT_THINKING_LABEL
  );
}

/** Set while a tool group header is showing the live state instead. */
const ThinkingInToolGroupContext = createContext(false);

/** Running-message indicator. */
const ThinkingIndicator: FC = () => {
  const label = useThinkingLabel();
  const { ThinkingHint } = useContext(ThreadComponentsContext);
  const shownInToolGroup = useContext(ThinkingInToolGroupContext);

  if (shownInToolGroup) return ThinkingHint ? <ThinkingHint /> : null;

  const indicator = (
    <span
      data-slot="aui_assistant-message-indicator"
      className="mcp-thinking text-muted-foreground"
      dir="rtl"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <span className="mcp-thinking__label">{label}</span>
      <span className="mcp-thinking__dots" aria-hidden>
        <span />
        <span />
        <span />
      </span>
    </span>
  );

  if (!ThinkingHint) return indicator;
  return (
    <div className="flex flex-col items-start gap-1">
      {indicator}
      <ThinkingHint />
    </div>
  );
};

const MessageError: FC = () => {
  const raw = useAuiState((s) => {
    const status = s.message.status;
    if (status.type !== "incomplete" || status.reason !== "error") {
      return undefined;
    }
    const error = status.error;
    if (typeof error === "string") return error;
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      return error.message;
    }
    return undefined;
  });
  const message =
    raw && isRawFetchFailure(raw) ? CHAT_NETWORK_ERROR_MESSAGE : raw;

  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root border-destructive bg-destructive/10 text-destructive dark:bg-destructive/5 mt-2 rounded-md border p-3 text-sm dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2">
          {message}
        </ErrorPrimitive.Message>
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const DefaultToolGroup: FC<PropsWithChildren<{ group: ThreadGroupPart }>> = ({
  group,
  children,
}) => {
  const currentTool = useToolGroupCurrentToolName(group.indices);
  const live = useToolGroupLiveState(group.indices);
  const thinkingLabel = useThinkingLabel();
  return (
    <ToolGroupBlock
      count={group.indices.length}
      active={live === "tool"}
      currentTool={currentTool}
      thinkingLabel={live === "thinking" ? thinkingLabel : undefined}
    >
      {children}
    </ToolGroupBlock>
  );
};

const AssistantMessage: FC = () => {
  const {
    ToolFallback: ToolFallbackComponent = ToolFallback,
    ToolGroup,
    ReasoningGroup,
    MessageFollowups: MessageFollowupsComponent,
    AssistantText: AssistantTextComponent = MarkdownText,
  } = useContext(ThreadComponentsContext);
  const [thinkingInToolGroup, setThinkingInToolGroup] = useState(false);

  return (
    <MessagePrimitive.Root
      data-slot="aui_assistant-message-root"
      data-role="assistant"
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 animate-in relative duration-150 [contain-intrinsic-size:auto_200px] [content-visibility:auto]"
    >
      <div
        data-slot="aui_assistant-message-content"
        className="text-foreground px-2 leading-relaxed wrap-break-word"
      >
        <ToolGroupThinkingContext.Provider value={setThinkingInToolGroup}>
        <ThinkingInToolGroupContext.Provider value={thinkingInToolGroup}>
        <MessagePrimitive.GroupedParts
          groupBy={groupPartByType({
            reasoning: ["group-chainOfThought", "group-reasoning"],
            "tool-call": ["group-chainOfThought", "group-tool"],
            "standalone-tool-call": [],
          })}
        >
          {({ part, children }) => {
            switch (part.type) {
              case "group-chainOfThought":
                return <div data-slot="aui_chain-of-thought">{children}</div>;
              case "group-tool":
                if (ToolGroup) {
                  return <ToolGroup group={part}>{children}</ToolGroup>;
                }
                return (
                  <DefaultToolGroup group={part}>{children}</DefaultToolGroup>
                );
              case "group-reasoning": {
                if (ReasoningGroup) {
                  return (
                    <ReasoningGroup group={part}>{children}</ReasoningGroup>
                  );
                }
                const running = part.status.type === "running";
                return (
                  <ReasoningRoot streaming={running}>
                    <ReasoningTrigger active={running} />
                    <ReasoningContent aria-busy={running}>
                      <ReasoningText>{children}</ReasoningText>
                    </ReasoningContent>
                  </ReasoningRoot>
                );
              }
              case "text":
                return <AssistantTextComponent />;
              case "reasoning":
                return <Reasoning {...part} />;
              case "tool-call":
                return part.toolUI ?? <ToolFallbackComponent {...part} />;
              case "data":
                return part.dataRendererUI;
              case "file":
                return (
                  <div data-slot="aui_assistant-message-file" className="py-1">
                    <File {...part} />
                  </div>
                );
              case "image":
                return (
                  <div data-slot="aui_assistant-message-image" className="py-1">
                    <Image {...part} />
                  </div>
                );
              case "indicator":
                return <ThinkingIndicator />;
              default:
                return null;
            }
          }}
        </MessagePrimitive.GroupedParts>
        </ThinkingInToolGroupContext.Provider>
        </ToolGroupThinkingContext.Provider>
        {MessageFollowupsComponent ? <MessageFollowupsComponent /> : null}
        <MessageError />
      </div>

      <div
        data-slot="aui_assistant-message-footer"
        className="aui-assistant-message-footer mt-2 flex min-h-6 items-center gap-1 overflow-visible px-1"
      >
        <MessageFeedbackHost>
          <BranchPicker />
          <AssistantActionBar />
        </MessageFeedbackHost>
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  const { AssistantActionExtra } = useContext(ThreadComponentsContext);
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="never"
      className="aui-assistant-action-bar-root text-muted-foreground flex gap-0.5 overflow-visible"
    >
      <MessageTimestamp className="aui-assistant-message-timestamp" />
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="کپی">
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon className="size-3.5 animate-in zoom-in-50 fade-in duration-200 ease-out" />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon className="size-3.5 animate-in zoom-in-75 fade-in duration-150" />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <MessageFeedbackButtons />
      {AssistantActionExtra ? <AssistantActionExtra /> : null}
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="تلاش مجدد">
          <RefreshCwIcon className="size-3.5" />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
};

const UserFilePart: FileMessagePartComponent = (part) => (
  <div data-slot="aui_user-message-file" className="py-1">
    <File {...part} />
  </div>
);

const UserImagePart: ImageMessagePartComponent = (part) => (
  <div data-slot="aui_user-message-image" className="py-1">
    <Image {...part} />
  </div>
);

const UserTextPart: TextMessagePartComponent = ({ text }) => {
  const visible =
    displayDomainMentionUserMessage(
      displaySuggestionUserMessage(
        displayPromptTemplateUserMessage(displaySkillUserMessage(text))
      )
    ) || text;
  if (!visible.trim()) return null;
  return <p className="aui-user-message-text m-0 whitespace-pre-wrap text-inherit">{visible}</p>;
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      data-slot="aui_user-message-root"
      className="aui-user-message-root fade-in slide-in-from-bottom-1 animate-in w-full px-2 duration-150 [contain-intrinsic-size:auto_200px] [content-visibility:auto]"
      data-role="user"
    >
      <div className="aui-user-message-content-wrapper">
        <UserMessageAttachments />
        <div className="aui-user-message-content mcp-user-bubble peer bg-muted rounded-xl px-4 py-2 wrap-break-word empty:hidden">
          <MessagePrimitive.Parts
            components={{
              Text: UserTextPart,
              File: UserFilePart,
              Image: UserImagePart,
            }}
          />
        </div>
      </div>

      <div className="aui-user-message-footer">
        <UserActionBar />
        <BranchPicker
          data-slot="aui_user-branch-picker"
          className="aui-user-branch-picker"
        />
      </div>
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="always"
      autohideFloat="always"
      className="aui-user-action-bar-root text-muted-foreground animate-in fade-in flex gap-0.5 duration-200"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip="ویرایش" className="aui-user-action-edit">
          <PencilIcon className="size-3.5" />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="کپی">
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon className="size-3.5 animate-in zoom-in-50 fade-in duration-200 ease-out" />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon className="size-3.5 animate-in zoom-in-75 fade-in duration-150" />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <MessageTimestamp className="aui-user-message-timestamp" />
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root
      data-slot="aui_edit-composer-wrapper"
      className="aui-user-message-root aui-edit-composer-wrapper fade-in slide-in-from-bottom-1 animate-in w-full px-2 duration-150 [contain-intrinsic-size:auto_200px] [content-visibility:auto]"
    >
      <div className="aui-user-message-content-wrapper">
        <ComposerPrimitive.Root className="aui-edit-composer-root aui-user-message-content cursor-text">
          <ComposerPrimitive.Input
            className="aui-edit-composer-input text-foreground w-full resize-none bg-transparent outline-none"
            autoFocus
          />
          <div className="aui-edit-composer-footer flex items-center justify-end gap-1.5 pt-1">
            <ComposerPrimitive.Cancel asChild>
              <button
                type="button"
                className="mcp-chat-pill-btn mcp-chat-pill-btn--ghost mcp-chat-pill-btn--sm"
              >
                لغو
              </button>
            </ComposerPrimitive.Cancel>
            <ComposerPrimitive.Send asChild>
              <button
                type="button"
                className="mcp-chat-pill-btn mcp-chat-pill-btn--primary mcp-chat-pill-btn--sm"
              >
                به‌روزرسانی
              </button>
            </ComposerPrimitive.Send>
          </div>
        </ComposerPrimitive.Root>
      </div>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root text-muted-foreground -ms-2 me-2 inline-flex items-center text-xs",
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="قبلی">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="بعدی">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
